import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, EmptyState, SkeletonTable } from '@/components/ui';
import { useCan } from '@/auth/consoleCapabilityContext';
import type { ScopedAccountRow } from '@/api/orgAdmin';
import { useScopedAccounts } from './Hierarchy/ScopedAccounts/useScopedAccounts';
import { useScopedAccountWrites } from './Hierarchy/ScopedAccounts/useScopedAccountWrites';
import { useScopedAccountViews } from './Hierarchy/ScopedAccounts/useScopedAccountViews';
import { useBulkLevelAssign } from './Hierarchy/ScopedAccounts/useBulkLevelAssign';
import { useRowTransfer } from './Hierarchy/ScopedAccounts/useRowTransfer';
import { useInlineLevelPicker } from './Hierarchy/ScopedAccounts/useInlineLevelPicker';
import { useScopedAccountExport } from './Hierarchy/ScopedAccounts/useScopedAccountExport';
import { useImportAccountsCsv } from './Hierarchy/ScopedAccounts/useImportAccountsCsv';
import { useProvisionAccount } from './Hierarchy/ScopedAccounts/useProvisionAccount';
import { ScopedAccountsTable } from './Hierarchy/ScopedAccounts/ScopedAccountsTable';
import { ScopedAccountDialogs } from './Hierarchy/ScopedAccounts/ScopedAccountDialogs';
import { BulkLevelAssignDialog } from './Hierarchy/ScopedAccounts/BulkLevelAssignDialog';
import { RowTransferDialog } from './Hierarchy/ScopedAccounts/RowTransferDialog';
import { ExportAccountsPopover } from './Hierarchy/ScopedAccounts/ExportAccountsPopover';
import { ImportAccountsCsvModal } from './Hierarchy/ScopedAccounts/ImportAccountsCsvModal';
import { ProvisionAccountModal } from './Hierarchy/ScopedAccounts/ProvisionAccountModal';
import { ScopedAccountProfileModal } from './Hierarchy/ScopedAccounts/ScopedAccountProfileModal';
import { AccountsScreen, type AccountsExtraTab } from './Accounts/Shared/AccountsScreen';
import { AccountsBulkBar, type AccountsBulkAction } from './Accounts/Shared/AccountsBulkBar';
import { AccountsPagination } from './Accounts/Shared/AccountsPagination';
import { AccountAuditPanel } from './Audit/AccountAuditPanel';
import type { ScopedAccountsTab } from './Hierarchy/ScopedAccounts/useScopedAccountViews';

const TABLE_COLUMNS = 6;

/**
 * EAA-1820 (screen unification pass): `/console/hierarchy/accounts` — the
 * org-scoped account browser, rendered through the SAME `AccountsScreen`
 * the realm `/console/accounts` uses (see `AccountsPage.tsx`'s docstring for
 * the full reversal note).
 *
 * WHAT STAYS DIFFERENT, per the ticket's own governing rule ("Khác nhau chỉ ở
 * ngữ cảnh"): the "Level & vai trò" column (`ScopedAccountsTable`, vs. the
 * realm's "Vai trò ứng dụng"), and every write flow (add/revoke/reset here are
 * Assignment-grant writes, not the realm's Keycloak-identity issue/reset/delete).
 *
 * EAA-1839 (2026-08-21, user decision — third and final direction on this
 * piece of chrome, see `useScopedAccountViews.ts` and the deleted
 * `Accounts/Shared/ScopeBar.tsx` for the first two): frame 35 draws a "Phạm
 * vi" band above the board (branch chip + "Gồm toàn bộ nhánh con" /
 * "Chỉ tài khoản trực thuộc" toggle). It is deliberately NOT built, for two
 * reasons. First, it is redundant: the rail's "Đơn vị" facet group
 * (`useScopedAccountFacets`'s `childOrg` group) already lists the scope org
 * itself as its first option (`directOrgLabel`, `child_organization_id` set
 * to the scope org id) alongside every direct child, and selecting that
 * option IS "Chỉ tài khoản trực thuộc" expressed as a filter —
 * `child_organization_id` REPLACES the effective scope rather than widening
 * it, so the toggle was a second way to say something the rail already says.
 * Second, the band cost the table vertical space the shell can't spare (see
 * `CLAUDE.md`'s "Console Content Fits The Shell" rule). `filters.includeChildren`
 * itself stays fixed `true` (`EMPTY_FILTERS`) with no UI control — see the
 * comment at its default in `types.ts` — because the "Đơn vị" facet group is
 * gated on it being true, not because the setting still needs a toggle.
 *
 * THE WAY BACK to `/console/hierarchy` is the left nav's "Phân cấp" item
 * (`ConsoleLayout.tsx`'s nav config, `console.nav.itemHierarchy`), present on
 * every console page including this one — removing the band strands nobody.
 * (It is NOT the console breadcrumb: this page never calls
 * `useConsoleBreadcrumb`, so `ConsoleTopbar`'s breadcrumb slot renders empty
 * here today — that half of the original claim did not hold and is not
 * repeated above.)
 *
 * EAA-1839 (frame 35 completion pass — this is the slice that closed frames 35
 * and 35a–35g). What each frame became, so the mapping is not re-derived later:
 *
 *  | frame | here |
 *  |---|---|
 *  | 35  | this page: tab strip with counts, six-group rail (sidebar or header placement), working search, the "Hiển thị" config popover, source column hidden by default, row tints. The frame's "Phạm vi" band and saved-filter row are deliberately NOT built — see `AccountsScreen`'s docstring |
 *  | 35a | `InlineLevelPicker` + `useInlineLevelPicker` — the amber "Chọn Level" chip's popover |
 *  | 35b | `ScopedAccountProfileModal` — the row's "Xem", or a click on the row |
 *  | 35c | `ImportAccountsCsvModal` + `useImportAccountsCsv` |
 *  | 35d | `ExportAccountsPopover` + `useScopedAccountExport` |
 *  | 35e | `ProvisionAccountModal`, mode `create` |
 *  | 35f | `ProvisionAccountModal`, mode `existing` |
 *  | 35g | the (i) beside the title — `PageHeader`'s collapsed subtitle, which is what frame 95's "Vỏ chuẩn" already specifies |
 *
 * TWO FRAMES ARE NOT IN THE DESIGN FILE and were built from the design changelog
 * plus the shape of their siblings: `35b Account profile` and `35f Add existing
 * account`. The 2026-08-21 "Dọn khối frame 35–35f bị nhân đôi" cleanup removed the
 * 35f artboard, and the surviving artboard labelled `35e Create account` in fact
 * draws the 35f mode (its segmented control shows "Thêm tài khoản đã có" active) —
 * so mode `create`'s field list comes from the changelog entry for 35e, not from a
 * drawing. Recorded here because the next session WILL open that file, find 35e
 * showing the wrong mode, and think this screen is wrong.
 *
 * BACKEND GAPS this screen degrades honestly rather than faking — each documented
 * at the hook that owns it: no `search` param on `GET /organizations/{id}/accounts`
 * (EAA-1839, 2026-08-21: search now works anyway, client-side — see
 * `useScopedAccounts`'s `searchWalkQuery`, the same walk-and-filter technique
 * `useScopedAccountExport.collectRows` already used for CSV, capped and the cap
 * surfaced by `searchCapNote` next to the range readout, not just in a comment),
 * no account import/export endpoints (both are done
 * client-side off the same paginated read, with their caps stated —
 * `useScopedAccountExport`, `useImportAccountsCsv`), no invitation entity (the
 * "Lời mời" tab), no provenance/last-login/2FA fields (the three inert rail groups
 * and two table columns), and the three bulk actions with no bulk endpoint.
 */
export function ScopedAccountsPage() {
    const { t } = useTranslation();
    const can = useCan();
    const vm = useScopedAccounts();
    const views = useScopedAccountViews(vm);
    const writes = useScopedAccountWrites(vm);
    const bulk = useBulkLevelAssign(vm);
    const rowTransfer = useRowTransfer(vm.invalidateList);
    const picker = useInlineLevelPicker(vm);
    const exporter = useScopedAccountExport(vm);
    const importer = useImportAccountsCsv(vm);
    const provision = useProvisionAccount(vm);
    const canViewAudit = can('console.audit.view');

    const [detailRow, setDetailRow] = useState<ScopedAccountRow | null>(null);

    if (!vm.canView) return null;

    const bulkActions: AccountsBulkAction[] = [
        {
            key: 'assign-level',
            label: t('console.hierarchy.scopedAccounts.bulkBar.assignLevelButton'),
            onClick: bulk.openDialog,
            disabled: Boolean(vm.bulkLevelDisabledReason),
            disabledReason: vm.bulkLevelDisabledReason ?? undefined,
        },
        {
            key: 'change-org',
            label: t('console.hierarchy.scopedAccounts.bulkBar.changeOrgButton'),
            disabled: true,
            disabledReason: t('console.hierarchy.scopedAccounts.bulkBar.noBulkChangeOrg'),
        },
        {
            key: 'reset-password',
            label: t('console.hierarchy.scopedAccounts.bulkBar.resetPasswordButton'),
            disabled: true,
            disabledReason: t('console.hierarchy.scopedAccounts.bulkBar.noBulkReset'),
        },
        // EAA-1839 (frame 35): the frame's fifth bulk action. `credential_mode:
        // 'invite'` on `POST /accounts` is the only thing in portal-api that sends
        // an invitation, and it only fires while CREATING an account — there is no
        // resend for an account that already exists.
        {
            key: 'resend-invite',
            label: t('console.hierarchy.scopedAccounts.bulkBar.resendInviteButton'),
            disabled: true,
            disabledReason: t('console.hierarchy.scopedAccounts.bulkBar.noResendInvite'),
        },
        {
            key: 'deactivate',
            label: t('console.hierarchy.scopedAccounts.bulkBar.deactivateButton'),
            disabled: true,
            disabledReason: t('console.hierarchy.scopedAccounts.bulkBar.noBulkDeactivate'),
        },
    ];

    const bulkNotes: string[] = [];
    // EAA-1817: this note used to warn that child-unit rows were being LEFT OUT.
    // They no longer are — the bulk assign runs one call per đơn vị — so it now
    // states where those rows will be written instead of excusing a silent gap.
    if (vm.crossOrgSelectedCount > 0) {
        bulkNotes.push(
            t('console.hierarchy.scopedAccounts.bulkBar.crossOrgNote', {
                value: vm.crossOrgSelectedCount.toLocaleString('vi-VN'),
            })
        );
    }
    if (vm.selectAllCapped) {
        bulkNotes.push(
            t('console.hierarchy.scopedAccounts.bulkBar.selectAllCappedNote', {
                value: vm.maxSelectAllRows.toLocaleString('vi-VN'),
            })
        );
    }
    if (vm.bulkLevelDisabledReason) bulkNotes.push(vm.bulkLevelDisabledReason);

    let body: React.ReactNode;
    if (!vm.orgId) {
        body = (
            <EmptyState
                title={t('console.accounts.scopedPage.noOrgSelectedTitle')}
                description={t('console.accounts.scopedPage.noOrgSelectedDescription')}
            />
        );
    } else if (vm.loading) {
        body = (
            <div role="status" aria-live="polite">
                <span className="portal-sr-only">{t('common.loading')}</span>
                <SkeletonTable rows={8} columns={TABLE_COLUMNS} />
            </div>
        );
    } else if (vm.isError) {
        body = (
            <p className="portal-admin-error">
                {t('console.accounts.scopedPage.loadErrorDescription')}{' '}
                <Button onClick={() => void vm.listQuery.refetch()}>
                    {t('console.accounts.page.retryButton')}
                </Button>
            </p>
        );
    } else if (vm.totalCount === 0) {
        body = (
            <EmptyState
                title={vm.filtersActive ? t('console.accounts.scopedPage.noMatchTitle') : t('console.accounts.scopedPage.emptyOrgTitle')}
                description={
                    vm.filtersActive
                        ? t('console.accounts.scopedPage.noMatchDescription')
                        : t('console.accounts.scopedPage.emptyOrgDescription')
                }
            />
        );
    } else {
        body = (
            <>
                <AccountsBulkBar
                    selectedCount={vm.selectedCount}
                    selectAllMatching={{
                        totalCount: vm.totalCount,
                        onSelectAll: () => void vm.selectAllMatching(),
                        busy: vm.selectAllBusy,
                        allSelected: vm.selectedCount >= vm.totalCount,
                    }}
                    actions={bulkActions}
                    onClear={vm.clearSelection}
                    notes={bulkNotes}
                />
                <ScopedAccountsTable
                    vm={vm}
                    views={views}
                    writes={writes}
                    picker={picker}
                    onTransfer={rowTransfer.open}
                    onViewDetail={setDetailRow}
                />
                <AccountsPagination
                    page={vm.page}
                    totalPages={vm.totalPages}
                    pageSize={vm.pageSize}
                    onPageSizeChange={vm.setPageSize}
                    rangeFrom={vm.rangeFrom}
                    rangeTo={vm.rangeTo}
                    total={vm.totalCount}
                    onGoTo={vm.setPage}
                    pageSizeOptions={vm.pageSizeOptions}
                />
            </>
        );
    }

    const extraTabs: AccountsExtraTab<ScopedAccountsTab>[] = [
        {
            key: 'no-level',
            label: t('console.hierarchy.scopedAccounts.tabs.noLevel'),
            count: views.noLevelCount,
            tone: 'warn',
        },
        {
            key: 'invites',
            label: t('console.hierarchy.scopedAccounts.tabs.invites'),
            // No invitation entity exists — see this file's docstring. The panel says
            // so instead of the tab pretending to hold a list.
            panel: (
                <EmptyState
                    title={t('console.hierarchy.scopedAccounts.tabs.invitesEmptyTitle')}
                    description={t('console.hierarchy.scopedAccounts.tabs.invitesEmptyDescription')}
                />
            ),
        },
    ];

    return (
        <>
            <AccountsScreen<ScopedAccountsTab>
                context="org"
                title={t('console.hierarchy.scopedAccounts.header.title')}
                subtitle={t('console.hierarchy.scopedAccounts.header.pageDescription')}
                headerActions={
                    <>
                        {vm.canManage && (
                            <Button onClick={importer.openModal}>
                                {t('console.hierarchy.scopedAccounts.header.importCsv')}
                            </Button>
                        )}
                        <ExportAccountsPopover vm={vm} exporter={exporter} />
                        {vm.canManage && (
                            <Button type="primary" onClick={() => provision.openModal()}>
                                {t('console.hierarchy.scopedAccounts.header.addAccountButton')}
                            </Button>
                        )}
                    </>
                }
                tab={views.tab}
                onTabChange={views.setTab}
                accountsTabCount={vm.totalCount}
                extraTabs={extraTabs}
                showAuditTab={canViewAudit}
                auditPanel={<AccountAuditPanel />}
                facetGroups={vm.facetGroups}
                onSelectFacet={vm.selectFacet}
                hasActiveFilters={vm.filtersActive}
                onClearFilters={vm.clearFilters}
                railQuickFilter={{ value: views.railQuery, onChange: views.setRailQuery }}
                railNote={t('console.hierarchy.scopedAccounts.facets.cumulativeNote')}
                placement={{ value: views.placement, onChange: views.setPlacement }}
                search={vm.search}
                onSearchChange={vm.setSearch}
                activeFilterCount={
                    (vm.filters.issue ? 1 : 0) +
                    (vm.filters.levelId ? 1 : 0) +
                    (vm.filters.childOrgId ? 1 : 0) +
                    (vm.filters.isActive !== null ? 1 : 0)
                }
                toolbarExtras={{
                    columns: { items: views.columnItems, onToggle: views.toggleColumn },
                    density: { value: views.density, onChange: views.setDensity },
                    onRefresh: vm.invalidateList,
                    refreshBusy: vm.listQuery.isFetching,
                    searchPlaceholder: t('console.hierarchy.scopedAccounts.header.searchPlaceholder'),
                    searchAriaLabel: t('console.hierarchy.scopedAccounts.header.searchAriaLabel'),
                    searchCapNote: vm.searchCapped
                        ? t('console.hierarchy.scopedAccounts.header.searchCapNote')
                        : undefined,
                }}
                body={body}
            />

            <ScopedAccountDialogs vm={vm} writes={writes} />
            <BulkLevelAssignDialog vm={vm} bulk={bulk} />
            <RowTransferDialog vm={rowTransfer} />
            <ImportAccountsCsvModal vm={vm} importer={importer} />
            <ProvisionAccountModal provision={provision} />
            <ScopedAccountProfileModal
                row={detailRow}
                onClose={() => setDetailRow(null)}
                onAssignLevel={(row) => {
                    setDetailRow(null);
                    picker.open(row);
                }}
                onReset={(row) => {
                    setDetailRow(null);
                    writes.setResetTarget(row);
                }}
                onTransfer={(row) => {
                    setDetailRow(null);
                    rowTransfer.open(row);
                }}
                onRevoke={(row) => {
                    setDetailRow(null);
                    writes.setRevokeTarget(row);
                }}
                canManage={vm.canManage}
                canReset={vm.canReset}
                orgName={vm.orgNameById.get(detailRow?.organization_id ?? '') ?? vm.orgName}
            />
        </>
    );
}

export default ScopedAccountsPage;
